import { css } from 'styled-components'
import { device, formInputTypes, VALIDATIONS } from 'heliocor-ui'
import isEmpty from 'lodash/isEmpty'
import { db } from '../../api/db'
import { cloneDeep } from 'lodash'
import { getParsedDependencies } from '../../utils/parsers'


const { PHONE } = VALIDATIONS

function createDocumentData({ document, party, kind }) {
    const { name, image, extraImage, selfieExtraImage } = document
    const { surname, nationalities, gender, addressCountry } = party

    let documentData = {
        type: name.toUpperCase(),
        image: image,
        ...(['ID_CARD'].includes(name) && { extraImage: extraImage }),
        ...(['SELFIE', 'VIDEO_SELFIE'].includes(name) && selfieExtraImage && { extraImage: selfieExtraImage }),
        ...(['PROOF_IDENTITY'].includes(kind) && { country: nationalities?.[0] }),
        ...(['PROOF_ADDRESS'].includes(kind) && { country: addressCountry?.code || addressCountry }),
        ...(['PROOF_IDENTITY', 'PROOF_ADDRESS'].includes(kind) && {
            name: party.name,
            surname: surname,
        }),
        ...(['PROOF_ADDRESS'].includes(kind) && {
            lang: addressCountry?.code || addressCountry,
        }),
        ...(['PROOF_IDENTITY'].includes(kind) && name !== 'DRIVING_LICENCE' && {
            gender: gender?.code || gender
        })
    }

    return documentData
}

async function verifyIndividualDocument({ documentData, name, kind }) {

    let url, docResponse, docUploaded, docToSave, error

    switch (kind) {
        case 'PROOF_IDENTITY':
            url = 'dokstor-cafe-api/api/document/process/identity'
            break
        case 'PROOF_ADDRESS':
            url = 'dokstor-cafe-api/api/document/process/address'
            break
        default:
            url = 'dokstor-cafe-api/api/document/process/selfie'
    }

    docUploaded = {
        type: name,
        datas: documentData
    }

    docResponse = await db('post', url, documentData)
        .catch(err => {
            console.error(err)
            error = err
        })

    if (docResponse && docResponse.errors) {
        error = docResponse.errors.map(err => err.code)
    } else {
        docToSave = { ...docResponse, ...docUploaded }
    }

    return docToSave || error
}

//-------- CUSTOM FIELDS HELPERS --------//

function typeSelector(type) {
    switch (type) {
        case 'RADIO':
            return formInputTypes.radio
        case 'SELECT':
        case 'MULTI_SELECT':
            return formInputTypes.select
        case 'BOOLEAN':
            return formInputTypes.check
        case 'DATE':
            return formInputTypes.date
        case 'NUMBER':
            return formInputTypes.inputNumber
        case 'CREDIT_CARD':
            return formInputTypes.creditCard
        case 'IBAN':
            return formInputTypes.iban
        case 'SORT':
            return formInputTypes.sortCode
        case 'SWIFT_BIC':
            return formInputTypes.bic
        case 'PHONE':
            return formInputTypes.telephone
        case 'CURRENCY':
            return formInputTypes.currency
        case 'TEXT':
        default:
            return formInputTypes.inputText
    }
}

const validationInjector = ({ field, options, kycType }) => {

    const { valueType, code, minLength, maxLength, validation } = field

    function injectMaxDateLimit() {
        const d = new Date();
        return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    }

    const requiredChecker = field.mandatory[kycType] ? ['REQUIRED'] : []

    const dataValidation = {
        YEAR: ['YEAR_IS_VALID'],
        MONTH: ['MONTH_IS_VALID'],
        DATE: ['DATE_IS_VALID']
    }

    const optionsAreValidObject = typeof options === 'object' && options !== null

    let validations = [...requiredChecker]
    if (options?.validations) validations = [...validations, ...options.validations]

    let max, debounceTiming, validationDependency

    switch (true) {
        case valueType === 'DATE' && optionsAreValidObject:
            validations.push(...dataValidation[options.format || 'DATE'])
            if (options?.validations?.includes('DATE_IS_IN_THE_FUTURE')) max = injectMaxDateLimit()
            break
        case valueType === 'DATE':
            validations.push('DATE_IS_VALID')
            break
        case code === 'CONTACT_EMAIL':
            validations.push('VALID_EMAIL')
            debounceTiming = 1000
            break
        case code === 'EMAIL':
            validations.push('VALID_EMAIL', 'EMAIL_IN_USE')
            debounceTiming = 1000
            break
        case code === 'PHONE':
            validations.push(PHONE)
            debounceTiming = 1000
            break
        default:
    }

    if (minLength) validations.push('MIN_LENGTH')
    if (maxLength) validations.push('MAX_LENGTH')
    if (validation) validations.push('REGEX_CHECK')

    return { validations, max, debounceTiming, validationDependency }
}

function parseCustomFields(orderedCategories, phase, formattingOptions, kycType, isCP) {

    let fields = new Map() 

    if (isEmpty(orderedCategories) || !kycType) return fields

    const actualPhase = phase === 'self-onboarding' ? 'CAFE_PHASE_1' : 'CAFE_PHASE_2'

    const dateExtraCss = css`
        flex-direction: column;
        justify-content: flex-start;
        @media ${device.midSize}{
            flex-direction: column;
            justify-content: flex-start;
            position: relative;
            display: flex;
        }
    `
    orderedCategories
        .sort((a, b) => a.order - b.order)
        .forEach(cat => {
            let extractOptions = options => options.map(option => ({
                value: option.value,
                label: option.name,
                code: option.code
            }))

            const catId = cat.code || cat.name

            fields[catId] = cat.fields.map((field, index) => {

                let isStandard = field.scope === 'STANDARD'
                let visibility = [], dependencies
                let validationDependency

                let deps = getParsedDependencies(field.dependencies)
                let options = field.options && JSON.parse(field.options)

                if (deps && !isEmpty(deps)) {
                    if (deps.type === 'SELECT') {
                        visibility.push({ field: deps.field, values: deps.values })
                    } else {
                        let specificDep
                        validationDependency = deps.field
                        specificDep = deps[actualPhase]

                        //TODO: Check if this is really needed
                        dependencies = {
                            [validationDependency]: {
                                ...(specificDep?.true && specificDep.true.mandatory ? { true: ['REQUIRED'] } : {}),
                                ...(specificDep?.false && specificDep.false.mandatory ? { false: ['REQUIRED'] } : {})
                            }
                        }

                        if (deps.kycTypesVisibility) {
                            dependencies['kycTypesVisibility'] = deps.kycTypesVisibility
                        }

                        if (specificDep?.true && !specificDep?.false) {
                            visibility.push({ field: validationDependency, value: true })
                        } else if (!specificDep?.true && specificDep?.false) {
                            visibility.push({ field: validationDependency, value: false })
                        }
                    }
                }

                if(field.valueType === 'CURRENCY') {
                    field.customOptions = field?.values?.map(value => ({
                        code: value.code,
                        value: value.value,
                        label: value.name
                    }))
                }

                if (field.valueType === 'SELECT' && options && options.subType && options.subType !== 'COUNTRY'){
                    field.valueType = options.subType
                }

                if(field.valueType === 'TEXT' && (field.code === 'PHONE' || options?.validations?.includes('PHONE'))) {
                    field.valueType = 'PHONE'
                }

                let customField = {
                    id: field.internalName,
                    type: typeSelector(field.valueType),
                    ...(isStandard && field.code ? { label: field.code } : { customLabel: field.name }),
                    ...(dependencies ? { dependencies } : {}),
                    ...(!isEmpty(visibility) ? { visibility } : {}),
                    ...(validationDependency ? { validationDependency } : {}),
                    ...(validationInjector({ field, options, deps, kycType })),
                    ...((options && options.currency) ? { currency: true } : {}),
                    ...(options && options.format ? { dateFormat: options.format } : {}),
                    ...(options ? { options } : {}),
                    ...(field.minLength ? { minLength: field.minLength } : {}),
                    ...(field.maxLength ? { maxLength: field.maxLength } : {}),
                    ...(field.validation ? { validation: field.validation } : {}),
                    ...(field.description ? { description: field.description } : {}),
                    ...(field.validationMessage ? { validationMessage: field.validationMessage } : {}),
                    editable: field.editable,
                    disabled: !isCP && ['EMAIL', 'CONTACT_EMAIL'].includes(field.code),
                    ...(field.valueType === 'DATE' && ((options && options.format !== 'YEAR') || !options) ? { styles: { input: dateExtraCss } } : {}),
                    ...(field.valueType === 'DATE' ? { formattingOptions: formattingOptions.dateFormat } : {}),
                    ...(field.valueType === 'CURRENCY' ? { customOptions:  field.customOptions} : {}),
                    ...(['SELECT', 'MULTI_SELECT', 'RADIO'].includes(field.valueType)
                        ? {
                            isMulti: field.valueType === 'MULTI_SELECT',
                            customOptions: field.values ? extractOptions(field.values, field.core) : [],
                        }
                        : {}
                    ),
                    scope: field.scope
                }
                return customField
            })
        })
    const companyLookup = {
        id: 'companyLookup',
        type: 'COMPANY_LOOKUP',
        disabled: [{ field: 'registeredAddressCountry' }, { field: 'name' }, { field: 'registrationNumber' }],
        extraValues: ['registeredAddressCountry', 'name', 'registrationNumber', 'region'],
        isAsync: true,
        isOpen: true,
        editable: true
    }

    Object.values(fields).forEach(cat => {
        const regNumberIndex = cat.findIndex(field => field.id === 'registrationNumber')
        if (regNumberIndex !== -1) cat.splice(regNumberIndex + 1, 0, { ...companyLookup })
    })
    return fields
}

function addCustomFieldsBeforeSave(fields, values) {
    const custom = {}
    const standardFields = {}

    for (let key in values) {
        for (let cat in fields) {
            const f = fields[cat].find(f => f.id === key)
            if (f) {
                if (f.scope === 'CUSTOM') {
                    if (!values[key]) {
                        custom[key] = ""
                    } else {
                        custom[key] = typeof values[key] === 'string' ? values[key] : JSON.stringify(values[key])
                    }
                } else {
                    standardFields[key] = values[key]
                }
            }
        }
    }
    return { ...standardFields, custom }
}


function addCustomFieldsBeforeSaveInStructure(fields, values) {
    const custom = {}
    const standardFields = {}

    for (let key in values) {
        for (let cat in fields) {
            const f = fields[cat].find(f => f.id === key)
            if (f && f.scope === 'CUSTOM') {
                if (!values[key]) {
                    custom[key] = ""
                } else {
                    custom[key] = typeof values[key] === 'string' ? values[key] : JSON.stringify(values[key])
                }
            } else {
                standardFields[key] = values[key]
            }
        }
    }
    return { ...standardFields, custom }
}

function addCustomCPFieldsBeforeSave(fields, values) {
    const custom = {}
    const standardFields = {}

    for (let key in values) {
        const f = fields.find(f => f.id === key)
        if (f && f.scope === 'CUSTOM') {
            custom[key] = typeof values[key] === 'string' ? values[key] : JSON.stringify(values[key])
        } else {
            standardFields[key] = values[key]
        }
    }
    return { ...standardFields, custom }
}

function parserValues(fields, values) {
    let newValues = {}

    Object.keys(values).forEach(key => {
        if (values[key] instanceof Object && !(values[key] instanceof Array)) return newValues[key] = values[key].value || values[key].identifier
        return newValues[key] = values[key]
    })

    return addCustomFieldsBeforeSave(fields, newValues)
}

function parserCPValues(fields, values) {
    let newValues = {}

    Object.keys(values).forEach(key => {
        if (values[key] instanceof Object && !(values[key] instanceof Array)) return newValues[key] = values[key].value || values[key].identifier
        return newValues[key] = values[key]
    })

    return addCustomCPFieldsBeforeSave(fields, newValues)
}

function parserStructureCPValues(fields, values) {
    let newValues = {}

    Object.keys(values).forEach(key => {
        if (values[key] instanceof Object && !(values[key] instanceof Array)) return newValues[key] = values[key].value || values[key].identifier
        return newValues[key] = values[key]
    })

    return addCustomFieldsBeforeSaveInStructure(fields, newValues)
}

function documentValidator({ responseVerification, retries, docToSave, name, docValidations}) {

    let reasonErrors = responseVerification?.servicesResponse?.reasons
    let error400 = responseVerification?.errorCode
    const _docValidations = cloneDeep(docValidations)
    let _updateRetries = true

    let errors = reasonErrors || [error400]

    const serviceResponseCorrect = responseVerification?.servicesResponse
    const statusResponse = serviceResponseCorrect?.status

    const newV = (docValidations[name] || 0) + 1
    
    _docValidations[name] = newV

    if (serviceResponseCorrect && statusResponse === 'VALID_AUTO') {

        docToSave.reasons = errors
        docToSave.autoValid = true

    } else if ((serviceResponseCorrect && statusResponse === 'INVALID_AUTO') || (responseVerification && responseVerification.errorCode)) {

        docToSave.errors = errors

    } else if (statusResponse === 'NOT_VALIDATED') {

        docToSave.errors = ['NOT_VALIDATED']

    } else if (Array.isArray(responseVerification)) {
        _updateRetries = false
        docToSave.errors = responseVerification
    }

    if (newV > retries) {
        docToSave.sentForManualValidation = true
    }

    return { docToSave, _updateRetries, _docValidations }
}

function getParsedCategoriesList(categories, fields){
    let categoriesList = []

    Object.keys(categories).forEach(cat => {
        const { name, description, code, dependencies = '{}' } = categories[cat] || {}
        const _dependencies = getParsedDependencies(dependencies)
        
        categoriesList.push({
            name,
            description,
            fields: fields[code || name],
            dependencies: _dependencies
        })
    })

    return categoriesList
}

export {
    createDocumentData,
    getParsedCategoriesList,
    parseCustomFields,
    verifyIndividualDocument,
    parserValues,
    parserCPValues,
    documentValidator,
    addCustomFieldsBeforeSave,
    parserStructureCPValues
}